import { useSignal } from '@preact/signals'
import { useEffect, useRef } from 'preact/hooks'
import Image from 'apps/website/components/Image.tsx'
import { invoke } from '$store/runtime.ts'
import AddToCartButtonVTEX from '$store/islands/AddToCartButton/vtex.tsx'
import { formatPrice } from 'deco-sites/dabelleultimate/sdk/format.ts'

export interface Suggestion {
	productID: number
	name: string
	image?: Array<{
		url: string
	}>
	url?: string
	description?: string
	sku?: string
	releaseDate?: string
	brand?: unknown
	category?: string
	additionalProperty?: unknown
	offers?: unknown
	inProductGroupWithID?: string
	isVariantOf?: unknown
}

interface Props {
	productGroupID: string
	billingDuration: number
	billingIncrement: number
}

export default function SuggestionFilterComponent({
	productGroupID,
	billingDuration,
	billingIncrement,
}: Props) {
	const selected = useSignal<string>('')
	const selectedProduct = useSignal<Suggestion | null>(null)
	const scrollIndex = useSignal(0)
	const filtered = useSignal<Suggestion[]>([])
	const all = useSignal<Suggestion[]>([])
	const isLoading = useSignal(true)
	const scrollRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		const id = Number(productGroupID)
		if (!id || isNaN(id)) {
			isLoading.value = false
			return
		}

		const fetchSuggestions = async () => {
			try {
				const result = await invoke['site'].loaders.suggestionFilter({
					productId: id,
				})

				const entries = Object.values(result ?? {})
				const variants = entries
					.flatMap((entry: any) => entry?.isVariantOf?.hasVariant || [])
					.map((variant: any, index: number) => ({
						productID: variant.productID,
						name: variant.name,
						image: variant.image,
						url: variant.url,
						description: variant.description,
						sku: variant.sku,
						releaseDate: variant.releaseDate,
						brand: variant.brand,
						category: variant.category,
						additionalProperty: variant.additionalProperty,
						offers: variant.offers,
						inProductGroupWithID: variant.inProductGroupWithID,
						alternateName: variant.alternateName,
						isDefaultSelected: index === 0,
					}))

				const firstProduct = variants[0]

				all.value = variants
				selected.value = String(firstProduct?.productID || '')
				selectedProduct.value = firstProduct
				scrollIndex.value = 0
			} catch (err) {
				console.error('[SuggestionFilter] Erro ao buscar:', err)
				all.value = []
			} finally {
				isLoading.value = false
			}
		}

		fetchSuggestions()
	}, [productGroupID])

	useEffect(() => {
		const filteredResult = selected.value
			? all.value.filter((p) => p.productID === Number(selected.value))
			: []

		filtered.value = filteredResult
	}, [selected.value, all.value])

	const scrollBy = (direction: 'next' | 'prev') => {
		const max = all.value.length - 1
		const current = scrollIndex.value
		const newIndex = direction === 'next'
			? Math.min(current + 1, max)
			: Math.max(current - 1, 0)

		scrollIndex.value = newIndex
		const nextProduct = all.value[newIndex]
		if (nextProduct) {
			selected.value = String(nextProduct.productID)
			selectedProduct.value = nextProduct
		}

		const el = scrollRef.current
		if (!el) return

		const step = 50
		el.scrollBy({
			left: direction === 'next' ? step : -step,
			behavior: 'smooth',
		})
	}

	if (isLoading.value) return <LoadingFallback />

	const selectedData = selectedProduct.value
	const offer = selectedData?.offers?.offers?.[0]
	const price = offer?.price
	const discount = offer?.priceSpecification?.find((p: any) => p.name === 'Desconto')?.price ||
		0

	const seller = offer?.seller

	return (
		<>
			{selectedData && (
				<section class='w-full max-w-[600px] mx-auto mt-8'>
					<h2 class='text-[#DA1884] font-bold mb-4 md:text-[20px] md:leading-[20.8px] text-[18px]'>
						Dica de amiga:
					</h2>

					<div class='flex flex-col gap-4 p-3 rounded-[8px] border md:border-[1px] border-[2px] border-[#DA1884]'>
						<div class='flex  md:flex-row gap-4 md:gap-6 items-start'>
							<div class='flex-shrink-0'>
								<Image
									src={selectedData.image?.[0]?.url ?? ''}
									alt={selectedData.name}
									width={124.5}
									height={124.58}
									class='w-[124.5px] h-[124.58px] object-cover'
									loading='lazy'
								/>
							</div>
							<div class='flex flex-col justify-center gap-1'>
								<h3 class='text-[#2C2824] font-bold md:text-[15.375px] text-[15.5px] leading-[19.2px] line-clamp-2 h-10'>
									{selectedData.name}
								</h3>
								{price && (
									<p class='text-[#DA1884] md:text-[15px] text-[15px] font-normal leading-[24px]'>
										R$ {price.toFixed(2).replace('.', ',')}
									</p>
								)}

								<p class='text-[#DA1884] italic font-bold text-[15px] leading-[24px]'>
									ou {billingDuration}x de {formatPrice(billingIncrement)}{' '}
									sem juros
								</p>
							</div>
						</div>

						<div class='flex gap-4'>
							<div class='relative w-full max-w-[350px] overflow-hidden'>
								<button
									class='absolute left-0 top-1/2 -translate-y-1/2 z-10  rounded-full w-[32px] h-[32px] flex items-center justify-start'
									onClick={() => scrollBy('prev')}
									aria-label='Anterior'
									dangerouslySetInnerHTML={{
										__html: `
                      <svg xmlns="http://www.w3.org/2000/svg" width="15" height="16" viewBox="0 0 15 16" fill="none">
                        <path d="M3.82391 7.09432L9.51758 1.40068C9.79218 1.12608 10.2374 1.12608 10.5119 1.40068L11.176 2.06475C11.4501 2.33888 11.4507 2.78317 11.1772 3.05795L6.66485 7.59152L11.1772 12.1251C11.4507 12.3998 11.4501 12.8441 11.176 13.1183L10.5119 13.7823C10.2373 14.0569 9.79215 14.0569 9.51758 13.7823L3.82394 8.08869C3.54934 7.81412 3.54934 7.36892 3.82391 7.09432Z" fill="#333333"/>
                      </svg>`,
									}}
								/>

								<button
									class='absolute right-0 top-1/2 -translate-y-1/2 z-10  rounded-full w-[32px] h-[32px] flex items-center justify-end'
									onClick={() => scrollBy('next')}
									aria-label='Próximo'
									dangerouslySetInnerHTML={{
										__html: `
                      <svg xmlns="http://www.w3.org/2000/svg" width="15" height="16" viewBox="0 0 15 16" fill="none">
                        <path d="M11.1761 8.08927L5.48242 13.7829C5.20782 14.0575 4.76263 14.0575 4.48806 13.7829L3.82398 13.1188C3.54985 12.8447 3.54933 12.4004 3.82281 12.1256L8.33515 7.59207L3.82281 3.05853C3.54933 2.78375 3.54985 2.33947 3.82398 2.06533L4.48806 1.40126C4.76266 1.12666 5.20785 1.12666 5.48242 1.40126L11.1761 7.0949C11.4507 7.36947 11.4507 7.81467 11.1761 8.08927Z" fill="#333333"/>
                      </svg>`,
									}}
								>
									›
								</button>

								<div
									ref={scrollRef}
									class='scrollbar-none snap-x snap-mandatory scroll-smooth overflow-x-auto flex gap-[20px] px-[40px] py-[3px] justify-start min-w-fit'
								>
									{all.value.map((item, index) => (
										<div
											key={item.productID}
											class='w-full snap-start shrink-0 max-w-[19px] max-h-[18px] lg:max-w-[30px] lg:max-h-[30px]'
										>
											<button
												class={`w-full border rounded-full overflow-hidden max-w-[19px] max-h-[18px] lg:max-w-[30px] lg:max-h-[30px] mx-auto ${
													selected.value === String(item.productID)
														? 'ring-2 ring-pink-500'
														: ''
												}`}
												onClick={() => {
													selected.value = String(item.productID)
													selectedProduct.value = item
													scrollIndex.value = index
												}}
												title={item.name}
											>
												<Image
													src={item.image?.[0]?.url ?? ''}
													alt={item.name}
													width={30}
													height={30}
													loading='lazy'
													class='object-cover w-full h-full rounded-full max-w-[19px] max-h-[18px] lg:max-w-[30px] lg:max-h-[30px]'
												/>
											</button>
										</div>
									))}
								</div>
							</div>
							<AddToCartButtonVTEX
								name={selectedData.name}
								productID={String(selectedData.productID)}
								productGroupID={selectedData.inProductGroupWithID ?? ''}
								price={price ?? 0}
								discount={discount}
								seller={seller ?? ''}
								quantity={1}
								class='bg-[#DA1884] w-full max-w-[133px] text-white text-sm font-bold px-4 rounded-md mx-auto'
							/>
						</div>
					</div>
				</section>
			)}
		</>
	)
}

export function LoadingFallback() {
	return (
		<section class='w-full max-w-[600px] mx-auto mt-8 animate-pulse'>
			<div class='h-[24px] w-[160px] bg-gray-200 rounded mb-4'></div>

			<div class='flex flex-col gap-4 p-4 rounded-[8px] border border-gray-200'>
				<div class='flex md:flex-row gap-4 items-center justify-start'>
					<div class='w-[124.5px] h-[124.58px] bg-gray-200 rounded'></div>

					<div class='flex flex-col gap-2 w-full max-w-[315px]'>
						<div class='h-[20px] bg-gray-200 rounded w-full' />
						<div class='h-[20px] bg-gray-200 rounded w-[60%]' />
						<div class='h-[20px] bg-gray-200 rounded w-[70%]' />
					</div>
				</div>

				<div class='w-full flex gap-4 items-center'>
					<div class='w-full flex gap-[20px] overflow-hidden py-2'>
						{Array.from({ length: 5 }).map((_, idx) => (
							<div
								key={idx}
								class='w-[18px] h-[18px] lg:w-[30px] lg:h-[30px] rounded-full bg-gray-200'
							/>
						))}
					</div>

					<div class='bg-gray-200 h-[40px] min-w-[133px] rounded'></div>
				</div>
			</div>
		</section>
	)
}
